import { useLocation, Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { CheckCircle, Calendar, Mail, ArrowRight, ExternalLink } from "lucide-react";

export default function NextSteps() {
  const location = useLocation();
  const navigate = useNavigate();
  const { formId, formType, formData, action } = location.state || {};

  const isConsultation = action === "consultation";
  const formTypeName = formType === "atopicnew" ? "New Page Assessment" : "Wikipedia Consultation";

  const handleBookConsultation = () => {
    navigate('/booking', { 
      state: { 
        formId,
        formType,
        formData
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-20 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-heading text-3xl text-gray-900 mb-4">
            {isConsultation ? "Almost There!" : "Submission Received!"}
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            {isConsultation 
              ? "Your form has been submitted. Complete your booking below to schedule your Zoom consultation."
              : "Thank you for your submission. We'll review your information and send you an email assessment shortly."
            }
          </p>
        </div>

        {/* Next Action Card */}
        {isConsultation ? (
          <Card className="bg-white border border-gray-200 shadow-sm mb-8">
            <CardContent className="p-8 text-center">
              <Calendar className="w-12 h-12 text-accent mx-auto mb-4" />
              <h2 className="font-heading text-2xl text-gray-900 mb-4">Schedule Your Consultation</h2>
              <p className="text-gray-600 mb-6">
                Complete your $150 consultation booking with David King. You'll receive both an email assessment and a 1-hour Zoom call.
              </p>
              <Button 
                onClick={handleBookConsultation}
                className="bg-accent text-black hover:bg-accent/90 font-semibold px-8 py-3"
                data-testid="book-now-btn"
              >
                Book Now - $150
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border border-gray-200 shadow-sm mb-8">
            <CardContent className="p-8 text-center">
              <Mail className="w-12 h-12 text-accent mx-auto mb-4" />
              <h2 className="font-heading text-2xl text-gray-900 mb-4">Check Your Email</h2>
              <p className="text-gray-600 mb-4">
                You will receive your Wikipedia assessment at <strong>{formData?.email}</strong> within 1-2 business days.
              </p>
              <p className="text-gray-500 text-sm">
                The assessment will evaluate whether your topic ({formData?.topic}) meets Wikipedia's notability criteria based on the articles you provided.
              </p>
            </CardContent>
          </Card>
        )}

        {/* FAQ Section */}
        <div className="mb-8">
          <h2 className="font-heading text-2xl text-gray-900 mb-6 text-center">Frequently Asked Questions</h2>
          
          <Accordion type="single" collapsible className="space-y-3">
            <AccordionItem value="item-1" className="bg-white border border-gray-200 rounded-lg">
              <AccordionTrigger className="px-6 py-4 text-left text-gray-900 hover:text-accent">
                Why did I complete this form?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-gray-600">
                <p className="mb-3">
                  There is often a history where the subject of the page created the Wikipedia article without any approval or oversight from Wikipedia's editors. Often, this was done many years ago.
                </p>
                <p className="mb-3">
                  You may have no knowledge of this and the page may have been created when Wikipedia's standards to qualify for a page were lower. Often, this raises questions as to whether the topic is actually famous enough to qualify for a page under Wikipedia's new standards.
                </p>
                <p>
                  The page may exist merely because no impartial editors at Wikipedia have noticed or scrutinized it. Without this assessment up-front, initial consultations are overly general and speculative. We need to know if the topic qualifies for a page before we can provide competent advice on next steps.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white border border-gray-200 rounded-lg">
              <AccordionTrigger className="px-6 py-4 text-left text-gray-900 hover:text-accent">
                What if my topic doesn't qualify for a page?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-gray-600">
                <p className="mb-3">You may choose to:</p>
                <ol className="list-decimal list-inside space-y-2">
                  <li><strong>Comply with Wikipedia's rules</strong> by suggesting the page be deleted</li>
                  <li><strong>Take your chances</strong> by requesting changes to the page anyway</li>
                  <li><strong>Play it safe</strong> by not drawing attention to the page where it may be scrutinized for the first time</li>
                </ol>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white border border-gray-200 rounded-lg">
              <AccordionTrigger className="px-6 py-4 text-left text-gray-900 hover:text-accent">
                What can I do to make my topic qualify for a page?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-gray-600">
                <p>
                  In 15 years of doing Wikipedia work, David King has never seen a company or person that didn't qualify for a page turn around an assessment with a media push to get the articles they need. Instead, we recommend focusing your time, energy, and budget in other areas.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white border border-gray-200 rounded-lg">
              <AccordionTrigger className="px-6 py-4 text-left text-gray-900 hover:text-accent">
                How come my competitor has a page?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-gray-600">
                <p>Your competitor may:</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>Be more famous than you</li>
                  <li>Have had the page created many years ago when Wikipedia's standards weren't as high</li>
                  <li>Have created the page using illegal, covert tactics</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white border border-gray-200 rounded-lg">
              <AccordionTrigger className="px-6 py-4 text-left text-gray-900 hover:text-accent">
                How much does it cost?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-gray-600">
                <p className="mb-3">
                  We charge a simple flat-fee that ranges from <strong>$3-9k</strong> to overhaul a page or <strong>$1,200 - $2,400</strong> for smaller changes.
                </p>
                <p className="mb-3">
                  $9,000 is what a Fortune 500 company with hundreds of years of history and entire books on them would pay for a massive Wikipedia page with hundreds of citations. $3-5k is a more common fee for mid-sized pages.
                </p>
                <p>
                  This fee includes researching, writing, and wikicoding a draft; consulting on Wikipedia's rules as it goes through internal review; and coaching you through the submission process. If the page is approved, we also monitor the page, support updates as needed, and provide other support for one year from the contract date.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="bg-white border border-gray-200 rounded-lg">
              <AccordionTrigger className="px-6 py-4 text-left text-gray-900 hover:text-accent">
                Why are competing firms cheaper?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-gray-600">
                <p>
                  Unfortunately, most Wikipedia firms are unethical, illegal operations. Many advertise they are ethical, while actually being banned from Wikipedia for unlawful, covert practices that take a lot less time and expertise. They often out-source to cheap labor in other countries with no Wikipedia expertise. Ethical Wiki is competitive with other legitimate providers.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="bg-white border border-gray-200 rounded-lg">
              <AccordionTrigger className="px-6 py-4 text-left text-gray-900 hover:text-accent">
                How do I know if a firm is ethical?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-gray-600">
                <p className="mb-3">
                  The easiest way is to look at the "Disclosed Paid Editing" section of Wikipedia's list of Wikipedia consulting firms.
                </p>
                <a 
                  href="https://en.wikipedia.org/wiki/Wikipedia:List_of_paid_editing_companies#Disclosed_paid_editing" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-accent hover:underline mb-3"
                >
                  View Wikipedia's List of Paid Editing Companies
                  <ExternalLink className="w-4 h-4 ml-1" />
                </a>
                <p className="mb-2">
                  Firms in that section meet two basic criteria:
                </p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Wikipedia knows where the firm is on Wikipedia</li>
                  <li>Wikipedia hasn't blocked or banned the firm</li>
                </ol>
                <p className="mt-3 text-sm text-gray-500 italic">
                  Note: Over 90% of the Wikipedia firms on Wikipedia's list do not meet this very basic criteria.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" className="bg-white border border-gray-200 rounded-lg">
              <AccordionTrigger className="px-6 py-4 text-left text-gray-900 hover:text-accent">
                How long does it take?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-gray-600">
                <p>
                  The contract gives us 30 days to create the first draft, but in most cases it will be done within a week or two. How long it takes for client approvals and Wikipedia review is outside our control. Wikipedians often accept/reject obvious submissions within a week, but take months to assess a close call.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Return Home */}
        <div className="text-center">
          <Link to="/">
            <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
              Return to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
